import React from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Table, Tooltip, Typography } from 'antd';
import { ColumnsType } from 'antd/es/table';
import debounce from 'lodash/debounce';

import { InputNumberForm } from '@/components/form/InputNumber';

const { Text, Title } = Typography;

interface IGravTable {
  handleChange: () => void;
  isObserver: boolean;
}

const nullValuesObject: Record<string, null> = {
  tgrav1: null,
  tgrav2: null,
  hgrav: null,
  Pgravi: null,
};

// Расчет гравитационного давления
export const GravTable: React.FC<IGravTable> = ({
  handleChange,
  isObserver,
}) => {
  const { control, reset, setValue, getValues } = useFormContext();

  const { fields, append, remove, insert } = useFieldArray({
    control,
    name: 'grav_table',
  });

  const handleAddRow = (index: number) => {
    const newData: Record<string, string | number>[] = [
      ...getValues('grav_table'),
    ];
    newData.splice(index + 1, 0, {
      ...nullValuesObject,
      i: fields.length + 1,
    });
    setValue('grav_table', newData);

    handleChange();
  };

  const handleRemoveRow = (index: number) => {
    const newData: Record<string, string | number>[] = [
      ...getValues('grav_table'),
    ];
    newData.splice(index, 1);
    setValue('grav_table', newData);
    handleChange();
  };

  const checkBlurFunction = (
    index: number,
    key: string,
    event: React.FocusEvent<HTMLInputElement>
  ) => {
    const obj: any = fields[index];
    if (obj[key] === null && event?.target?.value === '') return;
    if (obj[key] !== event?.target?.value) {
      handleChange();
    }
  };

  const blurFunction = debounce(checkBlurFunction, 1000);

  const columns: ColumnsType = [
    {
      title: '№',
      dataIndex: 'i',
      align: 'center',
      render: (text: string, record: any, index: number) => (
        <Text className='text-nowrap'>{index + 1}</Text>
      ),
    },
    {
      title: 'Температура 1, C',
      dataIndex: 'tgrav1',
      render: (text: string, record: any, index: number) => (
        <InputNumberForm
          name={`grav_table[${index}].tgrav1`}
          onBlur={(event: React.FocusEvent<HTMLInputElement>) =>
            blurFunction(index, 'tgrav1', event)
          }
          disabled={isObserver}
          size='middle'
          fixValue={1}
        />
      ),
    },
    {
      title: 'Температура 2, C',
      dataIndex: 'tgrav2',
      render: (text: string, record: any, index: number) => (
        <InputNumberForm
          name={`grav_table[${index}].tgrav2`}
          onBlur={(event: React.FocusEvent<HTMLInputElement>) =>
            blurFunction(index, 'tgrav2', event)
          }
          disabled={isObserver}
          size='middle'
          fixValue={1}
        />
      ),
    },
    {
      title: 'Высота воздушного столба, м',
      dataIndex: 'hgrav',
      render: (text: string, record: any, index: number) => (
        <Tooltip
          placement='top'
          title={
            <span>
              Ввести знак "+" или "-" в зависимости от направления. <br />
              Знак "-" если давление "помогает"
            </span>
          }
        >
          <InputNumberForm
            name={`grav_table[${index}].hgrav`}
            onBlur={(event: React.FocusEvent<HTMLInputElement>) =>
              blurFunction(index, 'hgrav', event)
            }
            disabled={isObserver}
            size='middle'
            fixValue={1}
          />
        </Tooltip>
      ),
    },
    {
      title: 'Гравитационное давление, Па',
      dataIndex: 'Pgravi',
      render: (text: string, record: any, index: number) => (
        <InputNumberForm
          name={`grav_table[${index}].Pgravi`}
          disabled
          size='middle'
          fixValue={1}
        />
      ),
    },
    {
      title: '',
      dataIndex: 'i',
      render: (text: string, record: any, index: number) => (
        <div className='flex gap-2'>
          <Button
            icon={<PlusOutlined />}
            onClick={() => handleAddRow(index)}
            disabled={isObserver}
          />
          <Button
            disabled={fields?.length === 1 || isObserver}
            icon={<DeleteOutlined />}
            onClick={() => handleRemoveRow(index)}
          />
        </div>
      ),
    },
  ];

  return (
    <Table
      rowKey={(record) => record?.id}
      dataSource={fields}
      columns={columns}
      pagination={false}
      scroll={{ x: 500 }}
      size='small'
      className='w-fit text-nowrap'
    />
  );
};
